import { patronApi } from "@api/patrons";
import { EditButton } from "@components/backoffice/buttons/EditButton";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import {
  ScrollingMenu,
  ScrollingMenuItem,
} from "@components/backoffice/buttons/ScrollingMenu";
import ESSelectorLoanRequest from "@modules/ESSelector/ESSelectorLoanRequest";
import { serializePatron } from "@modules/ESSelector/serializer";
import { BackOfficeRoutes } from "@routes/urls";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Divider, Popup, Header } from "semantic-ui-react";
import { connect } from "react-redux";
import { sendSuccessNotification } from "@components/Notifications/actions";
import { DocumentWeedoutModal } from "../DocumentWeedoutModal";
import { orderApi } from "@api/acquisition";
import { documentRequestApi } from "@api/documentRequests";
import { eItemApi } from "@api/eitems";
import { borrowingRequestApi } from "@api/ill";
import { itemApi } from "@api/items";
import { loanApi } from "@api/loans";
import { invenioConfig } from "@config";

class DocumentActionMenu extends Component {
  constructor(props) {
    super(props);
    this.weedoutModalRef = React.createRef();
    this.deleteModalRef = React.createRef();
    this._isMounted = false;
    this.state = {
      hasRelations: false,
      checkingRelations: true,
      blockingRelations: {},
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.checkForRelations();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.document.metadata.pid !== this.props.document.metadata.pid) {
      this.checkForRelations();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  checkForRelations = async () => {
    const { document } = this.props;
    const documentPid = document.metadata.pid;

    this.setState({ checkingRelations: true });

    try {
      const loanStates = invenioConfig.CIRCULATION.loanRequestStates.concat(
        invenioConfig.CIRCULATION.loanActiveStates
      );

      const activeDocumentRequestStates = ["PENDING"];
      const activeOrderStates = ["PENDING", "ORDERED", "RECEIVED"];
      const activeBorrowingRequestStates = ["PENDING", "REQUESTED", "ON_LOAN"];

      const checkPromises = [
        loanApi.list(
          loanApi.query().withDocPid(documentPid).withState(loanStates).qs()
        ),
        itemApi.list(itemApi.query().withDocPid(documentPid).qs()),
        documentRequestApi.list(
          documentRequestApi
            .query()
            .withDocPid(documentPid)
            .withState(activeDocumentRequestStates)
            .qs()
        ),
        eItemApi.list(eItemApi.query().withDocPid(documentPid).qs()),
        borrowingRequestApi.list(
          borrowingRequestApi
            .query()
            .withDocPid(documentPid)
            .withState(activeBorrowingRequestStates)
            .qs()
        ),
        orderApi.list(
          orderApi
            .query()
            .withDocPid(documentPid)
            .withState(activeOrderStates)
            .qs()
        ),
      ];

      const results = await Promise.all(checkPromises);

      const apiNames = [
        "loans",
        "items",
        "documentRequests",
        "eItems",
        "borrowingRequests",
        "orders",
      ];

      const relationLabels = {
        loans: "Active Loans",
        items: "Physical Items",
        documentRequests: "Pending Book Requests",
        eItems: "E-Items",
        borrowingRequests: "Active Borrowing Requests",
        orders: "Active New Acquisition",
      };

      const blockingRelations = {};
      let hasAnyRelations = false;

      results.forEach((result, index) => {
        const apiName = apiNames[index];

        if (result.data) {
          if (result.data.hits) {
            if (result.data.hits.length > 0) {
              hasAnyRelations = true;
              blockingRelations[apiName] = {
                label: relationLabels[apiName],
                count: result.data.hits.length,
              };
            }
          }
          if (result.data.data && result.data.data.length > 0) {
            hasAnyRelations = true;
            blockingRelations[apiName] = {
              label: relationLabels[apiName],
              count: result.data.data.length,
            };
          }
        }
        if (
          result.data &&
          Array.isArray(result.data) &&
          result.data.length > 0
        ) {
          hasAnyRelations = true;
          blockingRelations[apiName] = {
            label: relationLabels[apiName],
            count: result.data.length,
          };
        }
        if (result.hits && result.hits.length > 0) {
          hasAnyRelations = true;
          blockingRelations[apiName] = {
            label: relationLabels[apiName],
            count: result.hits.length,
          };
        }
      });

      if (this._isMounted) {
        this.setState({
          hasRelations: hasAnyRelations,
          blockingRelations,
          checkingRelations: false,
        });
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({
          hasRelations: false,
          blockingRelations: {},
          checkingRelations: false,
        });
      }
    }
  };

  requestLoanButton = (
    <div>
      {/* THIS DIV MUST be here, without it the trigger does not work */}
    </div>
  );

  requestLoan = (
    patronPid,
    { requestEndDate = null, deliveryMethod = null } = {}
  ) => {
    const {
      document: {
        metadata: { pid: documentPid },
      },
      requestLoanForPatron,
    } = this.props;
    const optionalParams = {};
    if (!_isEmpty(requestEndDate)) {
      optionalParams.requestEndDate = requestEndDate;
    }
    if (!_isEmpty(deliveryMethod)) {
      optionalParams.deliveryMethod = deliveryMethod;
    }
    requestLoanForPatron(documentPid, patronPid, optionalParams);
  };

  handleWeedoutSuccess = (documentPid) => {
    this.props.dispatch(
      sendSuccessNotification(
        "Success!",
        "The document was successfully weeded"
      )
    );
    if (this.props.onDocumentUpdate) {
      this.props.onDocumentUpdate(documentPid, "weeded_out");
    }
  };

  render() {
    const { document, relations, offset } = this.props;
    const isWeededOut = document.metadata.weed_out;
    const { hasRelations, checkingRelations, blockingRelations } = this.state;

    return (
      <div className="bo-action-menu">
        <EditButton
          fluid
          to={BackOfficeRoutes.documentEditFor(document.metadata.pid)}
          text="Edit Document"
          disabled={isWeededOut}
        />

        {!isWeededOut ? (
          checkingRelations ? (
            // Show loading state while checking
            <NewButton
              text="Checking relations..."
              fluid
              disabled
              icon="remove"
              loading
              color="grey"
            />
          ) : hasRelations ? (
            // Show disabled button with popup when relations exist
            <Popup
              trigger={
                <div>
                  <NewButton
                    text={
                      <span style={{ fontWeight: "bold", marginLeft: "18px" }}>
                        Weed-Out Disabled
                      </span>
                    }
                    fluid
                    disabled
                    color="grey"
                  />
                </div>
              }
              content={
                <div style={{ maxWidth: "300px", textAlign: "left" }}>
                  <Header as="h4" style={{ textAlign: "left" }}>
                    Cannot weed out - Active relations found
                  </Header>
                  {Object.entries(blockingRelations).map(([key, relation]) => (
                    <div key={key} style={{ marginBottom: "0.5rem" }}>
                      <strong>{relation.label}:</strong> {relation.count}
                    </div>
                  ))}
                  <div
                    style={{
                      marginTop: "0.5rem",
                      fontSize: "0.9em",
                      fontStyle: "italic",
                    }}
                  >
                    Resolve these relations first to enable weed-out.
                  </div>
                </div>
              }
              wide="very"
              position="right center"
              on="hover"
            />
          ) : (
            <DocumentWeedoutModal
              document={document}
              relations={relations}
              onWeedoutSuccess={this.handleWeedoutSuccess}
            />
          )
        ) : (
          <div>
            <NewButton
              text={
                <span>
                  <span style={{ fontWeight: "bold", marginLeft: "15px" }}>
                    Already Weeded Out
                  </span>
                </span>
              }
              fluid
              disabled
              icon="checkmark"
              color="grey"
            />
          </div>
        )}

        <Divider horizontal>Create</Divider>

        <NewButton
          text={
            <span>
              <span style={{ fontWeight: "bold", marginLeft: "18px" }}>
                New Physical Copy
              </span>
            </span>
          }
          fluid
          disabled={isWeededOut}
          to={
            !isWeededOut
              ? {
                  pathname: BackOfficeRoutes.itemCreate,
                  state: { formData: { document_pid: document.metadata.pid } },
                }
              : undefined
          }
        />

        <NewButton
          text="New E-Item"
          fluid
          disabled={isWeededOut}
          to={
            !isWeededOut
              ? {
                  pathname: BackOfficeRoutes.eitemCreate,
                  state: { formData: { document_pid: document.metadata.pid } },
                }
              : undefined
          }
        />

        <ESSelectorLoanRequest
          trigger={this.requestLoanButton}
          query={patronApi.list}
          serializer={serializePatron}
          title={`Request a loan for document ${document.pid} on behalf of patron`}
          content="Search for the patron to whom the loan should be assigned"
          selectionInfoText="The loan will be assigned to the following patron"
          emptySelectionInfoText="No patrons selected yet"
          onSave={this.requestLoan}
          saveButtonContent="Perform request"
          disabled={isWeededOut}
        />
        <Divider horizontal>Navigation</Divider>
        <ScrollingMenu offset={offset}>
          <ScrollingMenuItem elementId="metadata" label="Metadata" />
          <ScrollingMenuItem elementId="loan-requests" label="Loan Requests" />
          <ScrollingMenuItem
            elementId="document-items"
            label="Physical Copies"
          />
          <ScrollingMenuItem elementId="document-eitems" label="E-Items" />
          <ScrollingMenuItem
            elementId="document-statistics"
            label="Statistics"
          />
        </ScrollingMenu>
      </div>
    );
  }
}

DocumentActionMenu.propTypes = {
  relations: PropTypes.object.isRequired,
  document: PropTypes.object.isRequired,
  requestLoanForPatron: PropTypes.func.isRequired,
  onDocumentUpdate: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  offset: PropTypes.number.isRequired,
};

DocumentActionMenu.defaultProps = {
  onDocumentUpdate: null,
};

export default connect()(DocumentActionMenu);
